import React from 'react';
import { Container, Typography, Box, Divider, Paper, Table, TableBody, TableCell, TableHead, TableRow, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h6" gutterBottom fontWeight={700} sx={{ color: 'primary.main' }}>
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children, sx }) => (
  <Typography variant="body1" paragraph color="text.secondary" sx={sx}>
    {children}
  </Typography>
);

const SubSection = ({ title, children }) => (
  <Box sx={{ mt: 2, mb: 2 }}>
    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
      {title}
    </Typography>
    {children}
  </Box>
);

const DataTable = ({ rows }) => (
  <Box sx={{ overflowX: 'auto', mb: 3 }}>
    <Table size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <TableHead>
        <TableRow sx={{ bgcolor: 'grey.50' }}>
          {rows[0].map((cell, i) => (
            <TableCell key={i} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{cell}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.slice(1).map((row, i) => (
          <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
            {row.map((cell, j) => (
              <TableCell key={j} sx={{ verticalAlign: 'top', py: 1.5 }}>{cell}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const ExternalLink = ({ href, children }) => (
  <MuiLink href={href} target="_blank" rel="noopener noreferrer" underline="hover">
    {children}
  </MuiLink>
);

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Adatkezelési tájékoztató – Wedigo</title>
      </Helmet>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Adatkezelési tájékoztató
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Utoljára frissítve: 2026. november 1.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Hatályos: 2026. május 12-től
          </Typography>

          <Divider sx={{ mb: 5 }} />

          {/* 1. AZ ADATKEZELŐ */}
          <Section number="1" title="Az adatkezelő">
            <P>
              <strong>Megnevezés:</strong> Wedigo Korlátolt Felelősségű Társaság (Wedigo Kft.)<br />
              <strong>Székhely:</strong> 7635 Pécs, Bálicsi út 41/B<br />
              <strong>Cégjegyzékszám:</strong> 02-09-089704 (Pécsi Törvényszék Cégbírósága)<br />
              <strong>Adószám:</strong> 33083784-2-02<br />
              <strong>Weboldal:</strong> <ExternalLink href="https://wedigo.hu">https://wedigo.hu</ExternalLink><br />
              <strong>Kapcsolat:</strong> <MuiLink href="mailto:wedigo@wedigo.hu">wedigo@wedigo.hu</MuiLink>
            </P>
            <P>
              A Wedigo egy esküvői szolgáltatásokat összekötő online piactér és esküvőtervező platform. Ügyfeleink
              és szolgáltatóink között kapcsolatot teremtünk foglalások, üzenetváltások és fizetések lebonyolításával.
              Jelen adatkezelési tájékoztató az Európai Unió 2016/679 általános adatvédelmi rendeletének (GDPR)
              és az információs önrendelkezési jogról és az információszabadságról szóló 2011. évi CXII. törvény
              (Infotv.) előírásai alapján tájékoztatja a felhasználókat az általunk végzett adatkezelésekről.
            </P>
          </Section>

          {/* 2. KEZELT SZEMÉLYES ADATOK */}
          <Section number="2" title="A kezelt személyes adatok köre">

            <SubSection title="2.1 Regisztrációkor megadott adatok">
              <P>
                A fiók létrehozásakor az alábbi adatokat kérjük be vagy gyűjtjük automatikusan:
              </P>
              <DataTable rows={[
                ['Adat', 'Kötelező?', 'Megjegyzés'],
                ['Keresztnév, vezetéknév', 'Igen', 'Azonosításhoz, kommunikációhoz'],
                ['E-mail cím', 'Igen', 'Bejelentkezés, értesítések'],
                ['Jelszó', 'Igen', 'Csak bcrypt hash formában tárolva, nyers szöveg soha'],
                ['Telefonszám', 'Nem', 'Csak ha a felhasználó megadja'],
                ['Felhasználó típusa', 'Igen', 'Ügyfél (CLIENT) vagy Szolgáltató (PROVIDER)'],
                ['Hírlevél-feliratkozás', 'Nem', 'Explicit hozzájárulás szükséges'],
              ]} />
            </SubSection>

            <SubSection title="2.2 Szolgáltatói profiladatok (kizárólag PROVIDER típusnál)">
              <DataTable rows={[
                ['Adat', 'Megjegyzés'],
                ['Vállalkozás neve', 'Publikusan megjelenített'],
                ['Bemutatkozó szöveg', 'Publikusan megjelenített'],
                ['Helyszín: irányítószám, város, megye, cím', 'Publikusan megjelenített (közelítőleg)'],
                ['Koordináták (szélességi/hosszúsági fok)', 'Geocoding-gal előállított, térképes megjelenítéshez'],
                ['Weboldal URL', 'Opcionális, publikusan megjelenített'],
                ['Árkategória (min./max.)', 'Publikusan megjelenített'],
                ['Nyitvatartás', 'Publikusan megjelenített'],
                ['Barion fiókadatok (Barion user ID, email)', 'Kizárólag fizetések lebonyolításához'],
              ]} />
            </SubSection>

            <SubSection title="2.2.1 Számlázási és fizetési adatok (szolgáltatóknál, előfizetés / kiemelés vásárlásakor)">
              <DataTable rows={[
                ['Adat', 'Megjegyzés'],
                ['Számlázási név, cím, adószám / közösségi adószám, számlázási e-mail cím', 'A Wedigo által kiállított számlákhoz (Billingo)'],
                ['Megvásárolt csomag, időszak, összeg, számlaszám', 'Számlázás, előfizetés kezelése'],
                ['Barion tranzakcióazonosító, fizetési státusz', 'A fizetés visszaigazolásához, visszatérítéshez'],
                ['Automatikus megújítás: a Barion által adott ismétlődő fizetési azonosító (token)', 'Csak ha a szolgáltató bekapcsolja az automatikus megújítást. A bankkártya adatait a Wedigo nem látja és nem tárolja.'],
              ]} />
            </SubSection>

            <SubSection title="2.3 Közösségi bejelentkezési adatok (Google / Facebook OAuth)">
              <P>
                Ha a felhasználó a <strong>„Bejelentkezés Google-lal"</strong> vagy{' '}
                <strong>„Bejelentkezés Facebookkal"</strong> funkciót választja, a következő adatokat
                kapjuk az adott platformtól a felhasználó által korábban megadott hozzájárulás alapján:
              </P>
              <DataTable rows={[
                ['Adat', 'Forrás', 'Felhasználás'],
                ['Egyedi OAuth azonosító', 'Google / Facebook', 'Fiók összekapcsolás, bejelentkezés'],
                ['E-mail cím', 'Google / Facebook', 'Fiók létrehozás vagy meglévő fiókhoz kötés'],
                ['Keresztnév, vezetéknév', 'Google / Facebook', 'Profil adatok'],
                ['Profilkép URL', 'Google / Facebook', 'Avatar megjelenítés'],
              ]} />
              <P>
                Ezeket az adatokat kizárólag a nyilvánosan engedélyezett hatáskörök alapján kezeljük.
                Jelszót, privát üzeneteket vagy egyéb érzékeny adatokat a közösségi platformokról nem kérünk le.
                Az OAuth bejelentkezési folyamatot a Google, illetve a Facebook saját szerverei kezelik;
                a Wedigo ezekhez a szerverekhez kizárólag az autentikációs token révén fér hozzá.
              </P>
            </SubSection>

            <SubSection title="2.4 Foglalási adatok">
              <DataTable rows={[
                ['Adat', 'Megjegyzés'],
                ['Kapcsolattartó neve, e-mail, telefonszám', 'A foglalási ajánlat és visszaigazolás küldéséhez'],
                ['Esemény dátuma, időpontja, időtartama', 'Az ajánlat elkészítéséhez'],
                ['Vendégszám, esemény típusa', 'Az ajánlat elkészítéséhez'],
                ['Esemény helyszíne (cím, város, irányítószám, koordináták)', 'Logisztikai tervezés, térképes megjelenítés'],
                ['Ajánlat összege, letét összege, fizetési státusz', 'Pénzügyi nyilvántartás'],
                ['Barion tranzakcióazonosító', 'Fizetési visszaigazoláshoz, esetleges visszatérítéshez'],
                ['Számlázási adatok (ha számlát kért)', 'Számviteli törvény alapján 8 évig megőrzendő'],
              ]} />
            </SubSection>

            <SubSection title="2.5 Üzenet- és kommunikációs adatok">
              <P>
                A platform beépített üzenetrendszerén keresztül küldött üzeneteket (szöveg, feladó, fogadó,
                időbélyeg, olvasottsági állapot) tároljuk a szolgáltatás nyújtásához. Az üzenetek tartalmát
                munkatársaink egyedileg nem olvassák; kizárólag anonim, aggregált statisztikák készülnek
                róluk (ld. 9. pont).
              </P>
            </SubSection>

            <SubSection title="2.6 Esküvőtervező adatok">
              <P>
                Az Esküvőtervező funkcióban tárolt adatok (esemény neve, dátuma, vendéglista, vendégek
                RSVP-státusza, költségvetési tételek, ültetésrend, feladatok, idővonal, egyedi meghívó
                beállítások) a felhasználó kizárólagos magánhasználatára szolgálnak. Ezek az adatok
                harmadik félnek nem kerülnek átadásra és marketing célra nem használjuk fel.
              </P>
            </SubSection>

            <SubSection title="2.7 Fórum-aktivitás">
              <P>
                Fórumon közzétett bejegyzések (szöveg, kategória, láthatóság), hozzászólások, reakciók
                és bejelentések. Az anonim beállítással közzétett bejegyzéseknél a felhasználó neve
                más látogatók számára nem jelenik meg, de az adatkezelő számára az szerzőség ismert marad
                a visszaélések megelőzése érdekében.
              </P>
            </SubSection>

            <SubSection title="2.8 Automatikusan gyűjtött adatok">
              <DataTable rows={[
                ['Adat', 'Gyűjtési módja', 'Célja'],
                ['IP-cím', 'Szerver naplófájl (Morgan middleware)', 'Biztonság, visszaélés-megelőzés, hibaelhárítás'],
                ['Böngésző típusa és verziója (User-Agent)', 'HTTP kérés fejléce', 'Kompatibilitás, hibaelhárítás'],
                ['Látogatott oldalak, kérés dátuma/időpontja', 'Szerver naplófájl', 'Teljesítmény-monitorozás'],
                ['Keresési lekérdezések a platformon', 'Meilisearch (saját szerveren)', 'Keresési funkció működtetése'],
                ['Google Maps interakciók', 'Google API', 'Helyszínkeresés, térkép megjelenítés'],
                ['reCAPTCHA értékelés', 'Google reCAPTCHA v3', 'Bot- és spam-védelem'],
              ]} />
            </SubSection>

            <SubSection title="2.9 Értesítési és beállítási adatok">
              <P>
                E-mail értesítési preferenciák (mely típusú eseményekről kér értesítést a felhasználó),
                adatvédelmi beállítások (telefonszám láthatósága a foglalásokban, keresőmotor-indexelés
                engedélyezése), hírlevél-feliratkozási státusz.
              </P>
            </SubSection>

          </Section>

          {/* 3. AZ ADATKEZELÉS CÉLJA ÉS JOGALAPJA */}
          <Section number="3" title="Az adatkezelés célja és jogalapja">
            <DataTable rows={[
              ['Adatkezelési cél', 'Jogalap (GDPR 6. cikk)', 'Érintett adatok'],
              [
                'Felhasználói fiók létrehozása és kezelése',
                '(1)b) Szerződés teljesítése',
                'Regisztrációs adatok',
              ],
              [
                'Foglalások lebonyolítása, ajánlatok küldése',
                '(1)b) Szerződés teljesítése',
                'Foglalási, kapcsolattartási adatok',
              ],
              [
                'Üzenetküldés a platform felhasználói között',
                '(1)b) Szerződés teljesítése',
                'Üzenet tartalom, feladó/fogadó',
              ],
              [
                'Online fizetések feldolgozása (Barion)',
                '(1)b) Szerződés teljesítése',
                'Fizetési adatok, tranzakcióazonosítók',
              ],
              [
                'Számlakiállítás (Billingo) és a számlaadatok NAV felé történő adatszolgáltatása',
                '(1)c) Jogi kötelezettség (számviteli tv., áfa tv.)',
                'Számlázási adatok',
              ],
              [
                'Előfizetés / kiemelés automatikus megújítása (mentett kártya terhelése)',
                '(1)a) Hozzájárulás (vásárláskor, visszavonható) és (1)b) Szerződés teljesítése',
                'Ismétlődő fizetési azonosító, előfizetés adatai',
              ],
              [
                'Csalásmegelőzés az online fizetéseknél (Base Barion Pixel)',
                '(1)f) A Barion Payment Zrt. jogos érdeke (csalásmegelőzés)',
                'Böngésző- és eszközadatok, IP-cím, oldallátogatási események (közvetlenül a Barionhoz kerülnek)',
              ],
              [
                'E-mail értesítések küldése (foglalás, üzenet stb.)',
                '(1)b) Szerződés teljesítése',
                'E-mail cím, neve, esemény adatai',
              ],
              [
                'Platform biztonsága, visszaélések megelőzése',
                '(1)f) Jogos érdek',
                'IP-cím, naplófájlok',
              ],
              [
                'Platform fejlesztése anonim, aggregált adatok alapján',
                '(1)f) Jogos érdek',
                'Anonim használati statisztikák (ld. 9. pont)',
              ],
              [
                'Látogatottsági és viselkedési analitika (Google Analytics, Microsoft Clarity)',
                '(1)a) Hozzájárulás',
                'Süti-azonosítók, oldalnézetek, kattintás- és görgetési adatok',
              ],
              [
                'Hírlevél és marketing kommunikáció',
                '(1)a) Hozzájárulás',
                'E-mail cím, neve',
              ],
              [
                'Keresőmotor-indexelés tiltása (opcionális)',
                '(1)a) Hozzájárulás (visszavonható)',
                'Profil láthatósági beállítás',
              ],
            ]} />

            <P>
              <strong>Jogos érdek (GDPR 6. cikk (1) f)) alkalmazásakor</strong> elvégeztük a szükséges
              érdekmérlegelést: az adatkezelés az érintett alapvető jogait és szabadságait nem sérti
              aránytalan mértékben, hiszen az adatok köre korlátozott, az adatkezelés célja legitim
              (platform működtetése, fejlesztése), és az érintetteknek tiltakozási joguk van.
            </P>
          </Section>

          {/* 4. INFRASTRUKTÚRA ÉS TÁRHELYSZOLGÁLTATÁS */}
          <Section number="4" title="Infrastruktúra és tárhelyszolgáltatás">
            <SubSection title="4.1 Hostinger VPS — szerverinfrastruktúra">
              <P>
                A Wedigo platform szerverinfrastruktúrája a <strong>Hostinger</strong> tárhelyszolgáltató
                Európai Unión belüli, <strong>németországi</strong> adatközpontjában üzemel. A platform
                összes felhasználói adata — adatbázis, fájlok, naplók — kizárólag ezeken a szervereken
                kerül tárolásra.
              </P>
              <P>
                Mivel a szerverek az Európai Unió területén találhatók, az adatkezelés teljes egészében
                a GDPR hatálya alá esik; harmadik országba (EU/EGT-n kívülre) adattovábbítás nem történik
                a Hostinger infrastruktúrája révén.
              </P>
              <P>
                Hostinger adatvédelmi tájékoztatója:{' '}
                <ExternalLink href="https://www.hostinger.com/privacy-policy">
                  hostinger.com/privacy-policy
                </ExternalLink>
              </P>
            </SubSection>

            <SubSection title="4.2 Adatbázis és cache réteg">
              <P>
                Az adatok tárolása <strong>PostgreSQL</strong> adatbázisban történik (PostGIS térbeli
                kiterjesztéssel). A gyorsítótárazáshoz és az e-mail küldési sorhoz{' '}
                <strong>Redis</strong> memória-adatbázist használunk. Mindkét rendszer kizárólag a
                Hostinger szervereken fut.
              </P>
            </SubSection>
          </Section>

          {/* 5. DNS-SZOLGÁLTATÁS */}
          <Section number="5" title="DNS-szolgáltatás és hálózati biztonság">
            <P>
              A wedigo.hu domain névfeloldását a <strong>Cloudflare</strong> DNS-szolgáltatása végzi.
              A Cloudflare kezeli az SSL/TLS tanúsítványok automatikus megújítását is (ACME DNS
              challenge módszerrel). A Cloudflare hálózati szinten látja a doménre érkező DNS-lekérdezéseket,
              azonban a titkosított tartalmat nem fér hozzá.
            </P>
            <P>
              Cloudflare adatvédelmi tájékoztatója:{' '}
              <ExternalLink href="https://www.cloudflare.com/privacypolicy/">
                cloudflare.com/privacypolicy
              </ExternalLink>
            </P>
          </Section>

          {/* 6. SÜTIK (COOKIES) */}
          <Section number="6" title="Sütik (Cookies)">
            <SubSection title="6.1 Első fél sütik — a Wedigo NEM használ munkamenet-sütiket">
              <P>
                A Wedigo platform <strong>nem állít be saját (first-party) HTTP-sütiket</strong> a
                böngészőben. A bejelentkezési állapot megőrzése JWT (JSON Web Token) alapú, amelyet
                a böngésző <em>localStorage</em> tárolójában tárolunk. Ez a token az{' '}
                <code>Authorization: Bearer</code> HTTP fejlécben kerül elküldésre minden API-kérés
                esetén — cookie-t ebből a célból nem alkalmazunk.
              </P>
              <P>
                A felhasználó süti-hozzájárulási döntése (elfogadás/elutasítás) szintén
                a böngésző <em>localStorage</em> tárolójában kerül mentésre{' '}
                (<code>wedigo_cookie_consent</code> kulcs alatt), nem HTTP-sütiként.
              </P>
            </SubSection>

            <SubSection title="6.2 Harmadik fél sütik">
              <P>
                Az alábbi harmadik fél szolgáltatások saját sütiket állíthatnak be a böngészőben,
                ha a felhasználó igénybe veszi a kapcsolódó funkciókat:
              </P>
              <DataTable rows={[
                ['Szolgáltató', 'Mikor jelenik meg?', 'Sütik célja', 'Tájékoztató'],
                [
                  'Google (OAuth)',
                  '"Bejelentkezés Google-lal" funkció használatakor',
                  'Autentikációs munkamenet kezelése a Google-nál',
                  'policies.google.com/privacy',
                ],
                [
                  'Google (Maps)',
                  'Térkép, helyszínkereső megjelenítésekor',
                  'Térkép működéséhez szükséges állapot',
                  'policies.google.com/privacy',
                ],
                [
                  'Google (reCAPTCHA v3)',
                  'Regisztrációs és bejelentkezési formokon',
                  'Bot-védelmi pontszám megállapítása',
                  'policies.google.com/privacy',
                ],
                [
                  'Facebook (OAuth)',
                  '"Bejelentkezés Facebookkal" funkció használatakor',
                  'Autentikációs munkamenet kezelése a Facebooknál',
                  'facebook.com/privacy/policy',
                ],
                [
                  'Cloudflare R2 (CDN)',
                  'Képek, fájlok betöltésekor (images.wedigo.hu)',
                  'CDN teljesítmény és biztonsági fejlécek',
                  'cloudflare.com/privacypolicy',
                ],
                [
                  'Barion Pixel (Base)',
                  'Minden oldalon',
                  'Csalásmegelőzés az online fizetéseknél (a Barion jogos érdeke, hozzájárulás nélkül). Marketing célú (Full) Barion Pixelt nem használunk.',
                  'barion.com/hu/adatvedelmi-tajekoztato',
                ],
                [
                  'Google Analytics 4',
                  'Csak hozzájárulás után (analitikai sütik elfogadásakor)',
                  'Látogatók azonosítása, oldalnézetek, konverziók mérése',
                  'policies.google.com/privacy',
                ],
                [
                  'Microsoft Clarity',
                  'Csak hozzájárulás után (analitikai sütik elfogadásakor)',
                  'Session recording, heatmap, viselkedési analitika',
                  'privacy.microsoft.com',
                ],
              ]} />
              <P>
                Ezek a sütik az adott harmadik fél adatvédelmi szabályzata szerint kezelődnek. A Wedigo
                ezekre a sütikre nem fér hozzá és azok tartalmát nem olvassa.
              </P>
              <P>
                Az analitikai sütiket (Google Analytics, Microsoft Clarity) az oldal kizárólag a
                felhasználó kifejezett hozzájárulása esetén tölti be. A hozzájárulás a cookie-banneren
                vagy a{' '}
                <Link to="/sutik" style={{ color: 'inherit' }}>Süti szabályzat</Link>
                {' '}oldalon bármikor visszavonható.
              </P>
            </SubSection>
          </Section>

          {/* 7. HARMADIK FÉL ADATFELDOLGOZÓK */}
          <Section number="7" title="Harmadik fél adatfeldolgozók">
            <P>
              Az alábbi harmadik fél szolgáltatókkal adatfeldolgozói szerződéses kapcsolatban állunk,
              vagy azok saját adatkezelőként vesznek részt az adatkezelésben. Személyes adatot harmadik
              félnek kizárólag az itt felsorolt célokra adunk át.
            </P>

            <SubSection title="7.1 Tárhelyszolgáltatás">
              <P>
                <strong>Hostinger</strong> — szerverinfrastruktúra (Németország, EU)<br />
                Szerepe: adatfeldolgozó. Az összes felhasználói adatot a Hostinger szerverei tárolják.<br />
                <ExternalLink href="https://www.hostinger.com/privacy-policy">Adatvédelmi tájékoztató</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.2 DNS, CDN és fájltárolás">
              <P>
                <strong>Cloudflare</strong> — DNS feloldás, SSL, Cloudflare R2 fájltárolás<br />
                Szerepe: adatfeldolgozó (DNS szinten) és saját adatkezelő (R2 fájlok).<br />
                A felhasználók által feltöltött képeket (profilkép, galéria) a Cloudflare R2
                tárhelyen tároljuk (<em>images.wedigo.hu</em>). A feltöltött fájlok neve,
                mérete és URL-je kerül kezelésre.<br />
                <ExternalLink href="https://www.cloudflare.com/privacypolicy/">Adatvédelmi tájékoztató</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.3 E-mail küldés">
              <P>
                <strong>MailerSend</strong> — tranzakciós e-mailek (regisztráció, foglalás visszaigazolás,
                jelszóemlékeztető, meghívók stb.)<br />
                Szerepe: adatfeldolgozó. A MailerSend szervereire átkerül a címzett e-mail címe,
                neve és az e-mail szövege. Az e-mail kézbesítési státuszát naplózzuk.<br />
                <ExternalLink href="https://www.mailersend.com/legal/privacy-policy">Adatvédelmi tájékoztató</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.4 Online fizetések (Barion)">
              <P>
                <strong>Barion Payment Zrt.</strong> (1083 Budapest, Bókay János utca 44–46. C5 ép. 8. em.;
                cégjegyzékszám: 01-10-048552; MNB engedély: H-EN-I-1064/2013) — online bankkártyás,
                Apple Pay, Google Pay és Barion Tárca fizetések lebonyolítása.<br />
                Szerepe: a fizetés lebonyolításában önálló adatkezelő. A kártyaadatokat kizárólag a Barion
                kezeli; a Wedigo azokat nem látja és nem tárolja.<br />
                <strong>Átadott adatok:</strong> a fizető neve, e-mail címe, számlázási címe, a vásárolt
                termék megnevezése és összege. <strong>Cél:</strong> a fizetési tranzakció lebonyolítása,
                visszaigazolása és a csalásmegelőzés. A Wedigo a tranzakcióazonosítót és a fizetési
                státuszt kapja vissza.<br />
                <strong>Automatikus megújítás:</strong> ha a szolgáltató vásárláskor bekapcsolja, a Barion
                elmenti a bankkártyát, a Wedigo csak egy ismétlődő fizetési azonosítót tárol, amellyel a
                megújításkor terhelést kezdeményez. Az automatikus megújítás kikapcsolásával a további
                terhelések megszűnnek.
              </P>
              <P>
                <strong>Base Barion Pixel:</strong> a Barion előírása szerint az oldalba beépített kód,
                amely csalásmegelőzési célból a böngésző- és eszközadatokat, valamint az oldallátogatási
                eseményeket közvetlenül a Barion szervereire küldi. Ezeket az adatokat a Wedigo nem
                gyűjti, nem tárolja és nem kezeli. Az adatkezelés jogalapja a Barion jogos érdeke
                (csalásmegelőzés); részletek a Barion adatkezelési tájékoztatójának 5.4. pontjában.
                Marketing célú (Full) Barion Pixelt nem használunk.<br />
                <ExternalLink href="https://www.barion.com/hu/adatvedelmi-tajekoztato/">A Barion adatkezelési tájékoztatója</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.5 Számlakiállítás">
              <P>
                <strong>Billingo</strong> — elektronikus számlakiállítás<br />
                Szerepe: adatfeldolgozó. A Wedigo által a szolgáltatóknak kiállított számlákhoz (előfizetés,
                kiemelés, jutalék) a számlán szereplő adatok (név, cím, adószám, e-mail cím, tételek,
                összeg) kerülnek átadásra. A Billingo a számla adatait a jogszabály szerint továbbítja a
                Nemzeti Adó- és Vámhivatal Online Számla rendszerébe, és a számlát e-mailben elküldi a vevőnek.<br />
                <ExternalLink href="https://www.billingo.hu/adatkezeles">Adatvédelmi tájékoztató</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.6 Google-szolgáltatások">
              <P>
                <strong>Google LLC</strong> — OAuth hitelesítés, Maps, reCAPTCHA v3<br />
                Szerepe: önálló adatkezelő (OAuth és reCAPTCHA esetén), adatfeldolgozó (Maps API).<br />
              </P>
              <P>
                <em>Google OAuth:</em> A „Bejelentkezés Google-lal" funkció során a Google szerverei
                állítanak ki egy hozzáférési tokent, amelynek segítségével lekérjük a felhasználó
                nyilvános profiladatait (név, e-mail cím, profilkép URL). A Google saját cookie-kat
                állít be a bejelentkezési munkamenet kezeléséhez.
              </P>
              <P>
                <em>Google Maps:</em> Helyszínkereső és térkép megjelenítéséhez a Google Maps JavaScript
                és Places API-ját használjuk. A lekérdezések az API-kulccsal azonosítva futnak;
                a Maps a böngésző IP-címét látja.
              </P>
              <P>
                <em>Google reCAPTCHA v3:</em> Regisztrációnál és bejelentkezésnél bot-védelmi célból
                alkalmazzuk. A reCAPTCHA háttérben gyűjt böngésző-viselkedési adatokat a kockázati
                pontszám megállapításához.
              </P>
              <P>
                <ExternalLink href="https://policies.google.com/privacy">Google adatvédelmi tájékoztató</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.7 Facebook OAuth">
              <P>
                <strong>Meta Platforms, Inc.</strong> — Facebook OAuth hitelesítés<br />
                Szerepe: önálló adatkezelő.<br />
                A „Bejelentkezés Facebookkal" funkció során a Facebook szerverei kezelik a
                hitelesítési folyamatot. A Wedigo kizárólag a felhasználó által nyilvánosan
                engedélyezett adatokat (név, e-mail cím, profilkép URL, Facebook-azonosító) kapja meg.
                A Facebook saját cookie-kat állíthat be a bejelentkezési folyamat során.<br />
                <ExternalLink href="https://www.facebook.com/privacy/policy">Facebook adatvédelmi tájékoztató</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.8 Látogatottsági analitika — Google Analytics 4">
              <P>
                <strong>Google LLC</strong> — látogatottsági és konverziós elemzés<br />
                Szerepe: önálló adatkezelő (a mért adatokat Google szerverein tárolja).<br />
                Jogalap: <strong>hozzájárulás</strong> (GDPR 6. cikk (1) a)) — a script kizárólag
                a felhasználó explicit elfogadása után töltődik be.<br />
                Kezelt adatok: pszeudo-anonim eszközazonosító (_ga süti), látogatott oldalak, kattintási
                események, munkamenet-adatok. A valódi IP-cím nem kerül tárolásra (Google IP-anonimizáció).<br />
                Megőrzési idő: 2 év (GA4 süti lejárata).<br />
                <ExternalLink href="https://policies.google.com/privacy">Google adatvédelmi tájékoztató</ExternalLink>
                {' '}·{' '}
                <ExternalLink href="https://tools.google.com/dlpage/gaoptout">Google Analytics opt-out bővítmény</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.9 Viselkedési analitika — Microsoft Clarity">
              <P>
                <strong>Microsoft Corporation</strong> — viselkedési analitika (heatmap, session recording)<br />
                Szerepe: önálló adatkezelő (az adatokat Microsoft Azure szerverein tárolja).<br />
                Jogalap: <strong>hozzájárulás</strong> (GDPR 6. cikk (1) a)) — a script kizárólag
                a felhasználó explicit elfogadása után töltődik be.<br />
                Kezelt adatok: egérmozgás, kattintás- és görgetési viselkedés, munkamenet-felvételek,
                eszközazonosítók. A Clarity automatikusan maszkolja a beviteli mezők tartalmát
                (jelszavak, személyes adatok).<br />
                Megőrzési idő: 1 év (_clck süti lejárata).<br />
                <ExternalLink href="https://privacy.microsoft.com/privacystatement">Microsoft adatvédelmi tájékoztató</ExternalLink>
                {' '}·{' '}
                <ExternalLink href="https://clarity.microsoft.com/privacy">Microsoft Clarity adatvédelem</ExternalLink>
              </P>
            </SubSection>

            <SubSection title="7.10 Keresőmotor">
              <P>
                <strong>Meilisearch</strong> — platformon belüli keresési funkció<br />
                Szerepe: adatfeldolgozó (saját hosztolt rendszer, EU-s szerveren).<br />
                A Meilisearch a Wedigo saját szerverén fut; adatok nem kerülnek külső félhez. Az index
                kizárólag a nyilvánosan látható szolgáltatói profil nevét és kategóriáját tartalmazza.
              </P>
            </SubSection>

          </Section>

          {/* 8. ANONIM ADATOK PLATFORMFEJLESZTÉSRE */}
          <Section number="8" title="Anonim adatok felhasználása platformfejlesztésre">
            <P>
              A Wedigo a platform folyamatos fejlesztése érdekében anonim, személyazonosításra
              alkalmatlan, aggregált adatokat elemez. Ezek a statisztikák nem köthetők vissza
              egyéni felhasználókhoz.
            </P>
            <P>
              Példák az elemzett anonim mutatókra:
            </P>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <li><Typography variant="body1" color="text.secondary">Foglalások átlagos konverziós aránya kategóriánként</Typography></li>
              <li><Typography variant="body1" color="text.secondary">Leggyakrabban keresett helyszínek és kategóriák</Typography></li>
              <li><Typography variant="body1" color="text.secondary">Funkciók használati gyakorisága (Esküvőtervező, fórum stb.)</Typography></li>
              <li><Typography variant="body1" color="text.secondary">Átlagos válaszidők (foglalási ajánlatra)</Typography></li>
              <li><Typography variant="body1" color="text.secondary">Értékelési átlagok és eloszlások</Typography></li>
            </Box>
            <P>
              <strong>Jogalap:</strong> jogos érdek (GDPR 6. cikk (1) f)) — a platform fejlesztése,
              versenyképességének megőrzése és a felhasználói élmény javítása.<br />
              <strong>Garancia:</strong> az elemzések aggregált szinten történnek; nyers személyes
              adatok (név, e-mail, üzenet szövege) nem kerülnek elemzési folyamatokba.
            </P>
          </Section>

          {/* 9. ÜZENET-TARTALMAK KEZELÉSE */}
          <Section number="9" title="Üzenet-tartalmak kezelése">
            <P>
              A platform beépített üzenetrendszerén keresztül küldött üzenetek szövege az adatbázisban
              tárolódik a szolgáltatás nyújtásának idejére. Az üzenet-tartalmak kezelésére az alábbi
              elvek vonatkoznak:
            </P>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>
                <Typography variant="body1" color="text.secondary">
                  <strong>Egyedi üzenetek nem olvashatók:</strong> A Wedigo munkatársai az egyes
                  felhasználók üzenetváltásait egyedileg nem olvashatják. A tartalom hozzáférése
                  kizárólag az érintett felhasználók számára engedélyezett.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Anonim, aggregált statisztikák:</strong> Az üzenet-rendszerről kizárólag
                  személyazonosításra alkalmatlan összesítő adatok készülhetnek (pl. átlagos
                  üzenethossz, átlagos válaszidő, üzenetek napi száma kategóriánként). Ezeket a
                  statisztikákat a kommunikációs funkciók fejlesztéséhez használjuk fel.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Teljes szöveges indexelés nélkül:</strong> Az üzenetek szövege keresőbe
                  vagy gépi tanulási modellbe egyedileg nem kerül betáplálásra.
                </Typography>
              </li>
            </Box>
            <P>
              <strong>Jogalap:</strong> jogos érdek (GDPR 6. cikk (1) f)) az aggregált statisztikákra.<br />
              <strong>Adatmegőrzés:</strong> Az üzenetek a fiók törléséig kerülnek megőrzésre,
              illetve a felhasználó kérésére korábbi időpontban is törölhetők.
            </P>
          </Section>

          {/* 10. ADATMEGŐRZÉSI IDŐK */}
          <Section number="10" title="Adatmegőrzési idők">
            <DataTable rows={[
              ['Adatkategória', 'Megőrzési idő', 'Indok'],
              ['Felhasználói fiók és profiladatok', 'Fiók törléséig', 'Szerződéses kötelezettség'],
              ['Foglalások és üzenetek', 'Fiók törléséig', 'Szerződéses kötelezettség'],
              ['Fizetési és számlázási adatok', '8 év', '2000. évi C. törvény (Számviteli tv.) 169. §'],
              ['Szerver naplófájlok (IP, User-Agent)', 'Maximum 90 nap', 'Biztonsági célú megőrzés, arányosság elve'],
              ['Jelszó-visszaállítási tokenek', '1 óra (lejárat)', 'Biztonsági megfontolás'],
              ['Fiókaktiváló tokenek', '1 óra (lejárat)', 'Biztonsági megfontolás'],
              ['Inaktív fiókok adatai', '3 év inaktivitás után törlés', 'Adattakarékosság elve (GDPR 5. cikk (1) e))'],
              ['Értékelések', 'Fiók törléséig (anonim formában megőrizhetők)', 'Platform integritása'],
            ]} />
            <P>
              Az adatmegőrzési idők lejárta után az érintett adatokat visszaállíthatatlanul töröljük
              vagy anonimizáljuk.
            </P>
          </Section>

          {/* 11. ADATBIZTONSÁG */}
          <Section number="11" title="Adatbiztonság">
            <P>
              A személyes adatok védelme érdekében az alábbi technikai és szervezési intézkedéseket alkalmazzuk:
            </P>
            <DataTable rows={[
              ['Intézkedés', 'Részletek'],
              ['Jelszótitkosítás', 'bcrypt algoritmus, cost factor 12 — nyers szövegű jelszó soha nem kerül tárolásra'],
              ['Adatátvitel titkosítása', 'HTTPS (TLS) minden kommunikációhoz; Let\'s Encrypt tanúsítvány'],
              ['Hitelesítési tokenek', 'JWT token 7 napos lejárattal; localStorage-ban tárolva, nem cookie-ban'],
              ['Hozzáférés-kontroll', 'Szerepkör alapú jogosultságkezelés (CLIENT, PROVIDER, ADMIN)'],
              ['Terhelés-korlátozás', 'Rate limiting: 15 percenként maximum 2000 kérés IP-nként (production)'],
              ['Adatbázis-mentések', 'Rendszeres automatikus biztonsági mentések'],
              ['Infrastruktúra-hardening', 'SSH kulcsalapú hozzáférés, root bejelentkezés tiltva, jelszavas auth tiltva'],
            ]} />
            <P>
              Adatbiztonsági incidens esetén a GDPR 33. cikke alapján 72 órán belül értesítjük a Nemzeti
              Adatvédelmi és Információszabadság Hatóságot (NAIH), és súlyos kockázat esetén az érintett
              felhasználókat is tájékoztatjuk.
            </P>
          </Section>

          {/* 12. A FELHASZNÁLÓK JOGAI */}
          <Section number="12" title="A felhasználók jogai">
            <P>
              A GDPR III. fejezete alapján a felhasználókat az alábbi jogok illetik meg:
            </P>
            <DataTable rows={[
              ['Jog', 'Tartalma'],
              ['Tájékoztatáshoz való jog (13–14. cikk)', 'Jelen tájékoztató keretében teljesítjük.'],
              ['Hozzáférési jog (15. cikk)', 'Kérheti tájékoztatást a kezelt személyes adatairól és azok másolatát.'],
              ['Helyesbítési jog (16. cikk)', 'Kérheti pontatlan adatainak kijavítását vagy hiányos adatok kiegészítését.'],
              ['Törlési jog / „elfeledtetéshez való jog" (17. cikk)', 'Kérheti személyes adatainak törlését (kivéve jogszabályi megőrzési kötelezettség esetén).'],
              ['Az adatkezelés korlátozásához való jog (18. cikk)', 'Bizonyos esetekben kérheti az adatkezelés felfüggesztését.'],
              ['Adathordozhatósághoz való jog (20. cikk)', 'Kérheti adatait géppel olvasható, strukturált formátumban (JSON/CSV).'],
              ['Tiltakozáshoz való jog (21. cikk)', 'Tiltakozhat a jogos érdeken alapuló adatkezelés ellen (pl. anonim statisztika-készítés).'],
              ['Hozzájárulás visszavonása (7. cikk (3))', 'A hozzájáruláson alapuló adatkezelést (hírlevél) bármikor, következmény nélkül visszavonhatja.'],
            ]} />
            <P>
              <strong>Jogai érvényesítése:</strong> Írjon a{' '}
              <MuiLink href="mailto:wedigo@wedigo.hu">wedigo@wedigo.hu</MuiLink> e-mail-re. Kérelmére
              a GDPR 12. cikke alapján <strong>30 napon belül</strong> válaszolunk. Indokolt esetben
              ez az időszak egy alkalommal, legfeljebb két hónappal meghosszabbítható, amelyről értesítjük.
            </P>
            <P>
              Egyes jogok a Beállítások oldalon (
              <Link to="/beallitasok" style={{ color: 'inherit' }}>
                /beallitasok
              </Link>
              ) önkiszolgáló módon is gyakorolhatók (hírlevél-leiratkozás, e-mail értesítési preferenciák,
              fiók törlése).
            </P>
          </Section>

          {/* 13. FELÜGYELETI HATÓSÁG */}
          <Section number="13" title="Panasztétel és felügyeleti hatóság">
            <P>
              Ha úgy ítéli meg, hogy az adatkezelés sérti a GDPR előírásait, jogosult panaszt tenni
              az illetékes adatvédelmi felügyeleti hatóságnál:
            </P>
            <P>
              <strong>Nemzeti Adatvédelmi és Információszabadság Hatóság (NAIH)</strong><br />
              Cím: 1055 Budapest, Falk Miksa utca 9-11.<br />
              Telefon: +36 (1) 391-1400<br />
              E-mail: <MuiLink href="mailto:ugyfelszolgalat@naih.hu">ugyfelszolgalat@naih.hu</MuiLink><br />
              Weboldal: <ExternalLink href="https://www.naih.hu">naih.hu</ExternalLink>
            </P>
            <P>
              A panasztétel nem érinti az egyéb közigazgatási vagy bírósági jogorvoslathoz való jogot.
            </P>
          </Section>

          {/* 14. TÁJÉKOZTATÓ MÓDOSÍTÁSA */}
          <Section number="14" title="A tájékoztató módosítása">
            <P>
              A Wedigo fenntartja a jogot jelen adatkezelési tájékoztató módosítására. Lényeges változás
              esetén a regisztrált felhasználókat e-mailben értesítjük, és a módosítás hatálybalépésének
              dátumát a tájékoztató tetején feltüntetjük. A platformot a módosítás hatálybalépése után
              tovább használva a felhasználó elfogadja az aktualizált tájékoztatót.
            </P>
          </Section>

          {/* 15. KAPCSOLAT */}
          <Section number="15" title="Kapcsolat">
            <P>
              Adatkezelési kérdésekkel, kérelmekkel keressen minket:<br />
              <strong>E-mail:</strong> <MuiLink href="mailto:wedigo@wedigo.hu">wedigo@wedigo.hu</MuiLink><br />
              <strong>Weboldal:</strong>{' '}
              <ExternalLink href="https://wedigo.hu">https://wedigo.hu</ExternalLink>
            </P>
          </Section>

        </Paper>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
